import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/react-hooks';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useBooleanFeatureFlag } from '@confluence/session-data';
import { getLogger } from '@confluence/logger';
import { useTransitionId } from '@confluence/route-manager';
import { useIsLivePage } from '@confluence/live-pages-utils/entry-points/useIsLivePage';

import { EDITOR_PRELOAD_DRAFT_CONTENT } from '../../featureFlags';

import { ConfluenceDraftQuery } from './ConfluenceDraftQuery.graphql';
import type {
	ConfluenceDraftQuery as ConfluenceDraftQueryResponse,
	ConfluenceDraftQueryVariables,
} from './__types__/ConfluenceDraftQuery';
import { CollabDraftQuery } from '@confluence/fabric-providers/entry-points/CollabDraftQuery';
import type {
	CollabDraftQueryRes,
	CollabDraftQueryVariables,
} from '@confluence/fabric-providers/entry-points/CollabDraftQuery';

import { getDraftContentAppearancePropertyQuery } from '@confluence/content-appearance';
import type {
	getDraftContentAppearanceProperty as getDraftContentAppearancePropertyType,
	getDraftContentAppearancePropertyVariables as getDraftContentAppearancePropertyVariablesType,
} from '@confluence/content-appearance';

export type UseInjectDraftContentProps = {
	contentId: string;
	collabEditInitialized: boolean;
	cloudId: string;
	contentType: string;
};

export type UseInjectDraftContent = {
	draftTitle: string | null;
	draftBody: string | null; // adf json of body content in string format,
	draftAppearance: string | null;
};

const logger = getLogger('native-collab');

const parseConfluenceDraftBody = (data: ConfluenceDraftQueryResponse | undefined) => {
	const content = data?.content?.nodes?.[0];
	const draftAppearance = content?.appearanceDraft?.nodes?.[0]?.value;
	return {
		draftTitle: content?.title ?? null,
		draftBody: content?.body?.atlas_doc_format?.value ?? null, // to-do: backend should support specifying to return full-schema compatible adf
		draftAppearance: draftAppearance ? JSON.parse(draftAppearance) : null,
	};
};

const parseCollabDraftBody = (
	draftData: CollabDraftQueryRes | undefined,
	appearanceData: getDraftContentAppearancePropertyType | undefined,
) => {
	const draft = draftData?.collabDraft;
	const appearanceContent = appearanceData?.content?.nodes?.[0];
	const draftAppearance = appearanceContent?.appearanceDraft?.nodes?.[0]?.value;

	return {
		draftBody: draft?.document ?? null,
		draftTitle: draft?.metadata?.title ?? null,
		draftAppearance: draftAppearance ? JSON.parse(draftAppearance) : null,
	};
};

/**
 * Fetch the draft title & body and insert into editor upon initial load, if collab service is not yet loaded
 * Draft content is synced with Confluence every 5 seconds and on closing the editor, thus should be reliably up-to-date
 * see preloadEditorData, ConfluenceDraftQuery will be preloaded on transition to immediately inject into editor
 */
export const useInjectDraftContent = ({
	contentId,
	collabEditInitialized,
	cloudId,
	contentType,
}: UseInjectDraftContentProps): UseInjectDraftContent => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isEditorSSREnabled = useBooleanFeatureFlag('confluence.frontend.editor.ssr');
	const transitionId = useTransitionId();
	const isInitialLoad = !transitionId;
	const shouldPreloadForSSR = isEditorSSREnabled && isInitialLoad;
	const isLivePage = useIsLivePage();
	const bodyPreloadingEnabled =
		useBooleanFeatureFlag(EDITOR_PRELOAD_DRAFT_CONTENT) || shouldPreloadForSSR || isLivePage;
	const draftLoadTime = useRef<number | null>(null);
	const shouldLoadDocumentFromGql = useBooleanFeatureFlag(
		'confluence.frontend.load-document-draft-from-graphql',
	);
	let initialDraft: UseInjectDraftContent = {
		draftBody: null,
		draftTitle: null,
		draftAppearance: null,
	};

	let hasConfluenceDraftQueryLoaded = false;
	let hasCollabDraftQueryLoaded = false;
	let errorFetchingDraft;
	let errorFetchingAppearance;

	const {
		data: confluenceDraftData,
		loading: confluenceDraftLoading,
		error: confluenceDraftError,
	} = useQuery<ConfluenceDraftQueryResponse, ConfluenceDraftQueryVariables>(
		ConfluenceDraftQuery, // preloaded in preloadEditorData, happy-path query should return data immediately
		{
			fetchPolicy: 'cache-first',
			variables: {
				contentId,
				status: ['draft'],
			},
			skip: shouldLoadDocumentFromGql || !bodyPreloadingEnabled,
		},
	);

	const documentAri = `ari:cloud:confluence:${cloudId}:${contentType}/${contentId}`;
	const {
		data: collabDraftData,
		loading: collabDraftLoading,
		error: collabDraftError,
	} = useQuery<CollabDraftQueryRes, CollabDraftQueryVariables>(CollabDraftQuery, {
		fetchPolicy: 'cache-first',
		variables: {
			id: documentAri,
			token: null,
		},
		skip: !shouldLoadDocumentFromGql || !bodyPreloadingEnabled,
	});

	const {
		data: appearanceData,
		loading: appearanceLoading,
		error: appearanceError,
	} = useQuery<
		getDraftContentAppearancePropertyType,
		getDraftContentAppearancePropertyVariablesType
	>(getDraftContentAppearancePropertyQuery, {
		fetchPolicy: 'cache-first',
		variables: {
			contentId,
			status: ['current', 'draft'],
		},
		skip: !bodyPreloadingEnabled || !shouldLoadDocumentFromGql,
	});

	if (shouldLoadDocumentFromGql) {
		hasCollabDraftQueryLoaded =
			!collabDraftLoading && !!collabDraftData && !!appearanceData && !appearanceLoading;
		errorFetchingAppearance = appearanceError;
		errorFetchingDraft = collabDraftError;
	} else {
		hasConfluenceDraftQueryLoaded = !confluenceDraftLoading && !!confluenceDraftData;
		errorFetchingDraft = confluenceDraftError;
	}

	initialDraft = useMemo(() => {
		return shouldLoadDocumentFromGql
			? parseCollabDraftBody(collabDraftData, appearanceData)
			: parseConfluenceDraftBody(confluenceDraftData);
	}, [confluenceDraftData, collabDraftData, appearanceData, shouldLoadDocumentFromGql]);

	useEffect(() => {
		if (!bodyPreloadingEnabled) return;

		if (!shouldLoadDocumentFromGql) {
			if (!errorFetchingDraft && hasConfluenceDraftQueryLoaded) {
				draftLoadTime.current = performance.now();
				logger.debug`Draft content retrieved from ConfluenceDraftQuery at ${performance.now() / 1000}`;
			}
		} else {
			if (!errorFetchingDraft && hasCollabDraftQueryLoaded && !errorFetchingAppearance) {
				draftLoadTime.current = performance.now();
				logger.debug`Draft content retrieved from CollabDraftQuery at ${performance.now() / 1000}`;
			}
		}
	}, [
		hasConfluenceDraftQueryLoaded,
		hasCollabDraftQueryLoaded,
		errorFetchingDraft,
		errorFetchingAppearance,
		bodyPreloadingEnabled,
		shouldLoadDocumentFromGql,
	]);

	useEffect(() => {
		if (collabEditInitialized && !!draftLoadTime.current) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'editorContentVisibleDisabledTime',
					actionSubject: 'nativeEditor',
					source: 'confluenceFrontend',
					objectId: contentId,
					attributes: {
						timeDiff: performance.now() - draftLoadTime.current,
					},
				},
			}).fire();
		}
		// don't depend on createAnalyticsEvent to avoid unnecessary re-triggers of this effect
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [collabEditInitialized, contentId]);

	return initialDraft;
};
